import { Injectable } from '@angular/core'
import { RouterStateSnapshot } from '@angular/router'

import { ROUTER_NAVIGATION, RouterNavigationAction, RouterNavigationPayload } from '@ngrx/router-store'
import { Action, select, Store } from '@ngrx/store'
import { Actions, Effect } from '@ngrx/effects'

import { Observable, of } from 'rxjs'
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { toPayload, withPredicateFrom } from '../../core/helpers/rx-pipes';

import { CartService } from '../services/cart-service'

import * as cart from '../actions/cart'
import * as fromCart from '../reducers'
import * as fromAuth from '../../auth/reducers'

import { AddCartEvent } from '../models/cart-models';

@Injectable()
export class CartEffects {
    constructor(
        private actions$: Actions,
        private store: Store<fromCart.State>,
        private cartSvc: CartService
    ) { }

    @Effect()
    getCartOnNavigate$: Observable<Action> =
        this.actions$.ofType<RouterNavigationAction<RouterStateSnapshot>>(ROUTER_NAVIGATION)
            .pipe(
                toPayload<RouterNavigationPayload<RouterStateSnapshot>>(),
                filter(payload => payload.routerState.url.indexOf('/logout') === -1),
                withPredicateFrom(this.store.pipe(select(fromAuth.isLoggedIn))),
                withPredicateFrom(this.store.pipe(select(fromAuth.isCustomerSelected))),
                map((router) => {
                  if (router.routerState.url.indexOf('/checkout') > -1) {
                    return new cart.Get(true)
                  } else {
                    return new cart.Get()
                  }
                })
            )

    @Effect()
    getCart$: Observable<Action> =
        this.actions$.ofType<cart.Get>(cart.GET)
        .pipe(
            withLatestFrom(
              this.store.pipe(select(fromAuth.getCustomerId)),
            ),
            switchMap(([action, customerId]) => {
              return this.cartSvc.getOpenCart(customerId, action.includeExtraCosts)
                    .pipe(
                        map(order => new cart.GetSuccess(order)),
                        catchError(e => of(new cart.GetFailed(e)))
                    )
            })
        )

    @Effect()
    addCart$: Observable<Action> =
        this.actions$.ofType<cart.Add>(cart.ADD)
            .pipe(
                toPayload<AddCartEvent>(),
                withLatestFrom(
                    this.store.pipe(select(fromCart.hasActiveCart)),
                    this.store.pipe(select(fromCart.getOrderId)),
                    this.store.pipe(select(fromAuth.getCustomerId)),
                    (line, active, orderId, customerId) => ({line, active, orderId, customerId})
                ),
                switchMap(state => {
                    return (
                        state.active
                            ? this.cartSvc.setLine(state.customerId, state.orderId, state.line)
                            : this.cartSvc.createOrder(state.customerId, state.line)
                    ).pipe(
                        map(order => new cart.AddSuccess(order)),
                        catchError(e => of(new cart.AddFailed(e)))
                    )
                })
            )

    @Effect()
    changeCart$: Observable<Action> =
        this.actions$.ofType<cart.Change>(cart.CHANGE)
            .pipe(
                toPayload<AddCartEvent>(),
                withLatestFrom(
                    this.store.pipe(select(fromCart.getOrderId)),
                    this.store.pipe(select(fromAuth.getCustomerId)),
                  (line, orderId, customerId) => ({line, orderId, customerId})
                ),
                switchMap(state => {
                    return this.cartSvc.setLine(state.customerId, state.orderId, state.line)
                        .pipe(
                            map(order => new cart.ChangeSuccess(order)),
                            catchError(e => of(new cart.ChangeFailed(e)))
                        )
                })
            )

    @Effect()
    deleteLine$: Observable<Action> =
        this.actions$.ofType<cart.Remove>(cart.REMOVE)
            .pipe(
                withLatestFrom(
                    this.store.pipe(select(fromCart.getOrderId)),
                    this.store.pipe(select(fromAuth.getCustomerId)),
                    (action, orderId, customerId) => ({ action, orderId, customerId })
                ),
                switchMap(state => {
                    return this.cartSvc.deleteLine(state.orderId, state.action.payload.lineId, state.customerId, state.action.payload.includeExtraCosts)
                        .pipe(
                            map(order => new cart.RemoveSuccess(order)),
                            catchError(e => of(new cart.RemoveFailed(e)))
                        )
                })
            )
}
